import { create } from "zustand";
import Keycloak from "keycloak-js";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.digitize.mr.jandayan.net";
const REALM_CONFIG_KEY = "digitize-realm-config";

let keycloak = null;
let initializedRealm = null;
let initPromise = null;

function getStoredRealmConfig() {
  try {
    const raw = sessionStorage.getItem(REALM_CONFIG_KEY);
    if (!raw) return null;
    const config = JSON.parse(raw);
    if (!config?.realm || !config?.keycloakUrl || !config?.clientId) return null;
    return config;
  } catch {
    return null;
  }
}

function storeRealmConfig(config) {
  sessionStorage.setItem(REALM_CONFIG_KEY, JSON.stringify(config));
}

function clearStoredRealmConfig() {
  sessionStorage.removeItem(REALM_CONFIG_KEY);
}

function getKeycloak(config = getStoredRealmConfig()) {
  if (!config) return null;

  const instanceKey = `${config.keycloakUrl}|${config.realm}|${config.clientId}`;
  if (!keycloak || initializedRealm !== instanceKey) {
    keycloak = new Keycloak({
      url: config.keycloakUrl,
      realm: config.realm,
      clientId: config.clientId,
    });
    initializedRealm = instanceKey;
    initPromise = null;
  }

  return keycloak;
}

function tokenUser(tokenParsed) {
  if (!tokenParsed) return null;
  return {
    email: tokenParsed.email,
    sub: tokenParsed.sub,
    preferred_username: tokenParsed.preferred_username,
    name: tokenParsed.name || [tokenParsed.given_name, tokenParsed.family_name].filter(Boolean).join(" "),
    realm_access: tokenParsed.realm_access,
    resource_access: tokenParsed.resource_access,
  };
}

function persistTokens(instance) {
  if (instance?.token) {
    localStorage.setItem("accessToken", instance.token);
  }
  if (instance?.refreshToken) {
    localStorage.setItem("refreshToken", instance.refreshToken);
  }
}

function clearTokens() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
}

export const useAuthStore = create((set, get) => ({
  user: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  keycloak: null,

  setUser: (user) => set({ user }),
  clearAuth: () => {
    clearTokens();
    set({ user: null, error: null, isAuthenticated: false, keycloak });
  },

  init: async () => {
    const realmConfig = getStoredRealmConfig();
    const instance = getKeycloak(realmConfig);

    if (!instance) {
      clearTokens();
      set({ isLoading: false, isAuthenticated: false, user: null, keycloak: null });
      return false;
    }

    if (instance.authenticated) {
      persistTokens(instance);
      set({
        user: tokenUser(instance.tokenParsed),
        isAuthenticated: true,
        isLoading: false,
        keycloak: instance,
      });
      return true;
    }

    if (initPromise) return initPromise;

    initPromise = (async () => {
      set({ isLoading: true, error: null, keycloak: instance });
      try {
        const authenticated = await instance.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        });

        if (authenticated) {
          persistTokens(instance);
          instance.onTokenExpired = () => {
            get().refreshToken().catch(() => {
              get().clearAuth();
              window.dispatchEvent(new CustomEvent("auth:logout"));
            });
          };
        } else {
          clearTokens();
        }

        set({
          user: authenticated ? tokenUser(instance.tokenParsed) : null,
          isAuthenticated: authenticated,
          isLoading: false,
          keycloak: instance,
        });

        return authenticated;
      } catch (err) {
        clearTokens();
        set({
          error: err.message || "Keycloak initialization failed",
          isAuthenticated: false,
          isLoading: false,
          keycloak: instance,
        });
        throw err;
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  },

  discoverRealm: async (email) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/realm-discovery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Unable to find a network for this account");
    }

    storeRealmConfig(data);
    // Initialize Keycloak instance immediately after discovering the realm
    const instance = getKeycloak(data);
    if (instance) {
      set({ keycloak: instance });
    }
    return data;
  },

  loginWithEmail: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const realmConfig = await get().discoverRealm(email);
      const instance = get().keycloak || getKeycloak(realmConfig);
      if (!instance) {
        throw new Error("Failed to initialize Keycloak for this network");
      }
      set({ keycloak: instance });
      await instance.login({ loginHint: email, redirectUri: window.location.origin });
    } catch (err) {
      set({ error: err.message || "Login failed", isLoading: false });
      throw err;
    }
  },

  login: async () => {
    const instance = getKeycloak();
    if (!instance) {
      throw new Error("Enter your email first so we can find your network");
    }
    await instance.login();
  },

  logout: async () => {
    const instance = keycloak;
    clearStoredRealmConfig();
    get().clearAuth();
    if (instance) {
      await instance.logout({ redirectUri: window.location.origin });
    }
  },

  getToken: () => keycloak?.token || localStorage.getItem("accessToken"),

  refreshToken: async () => {
    const instance = getKeycloak();
    if (!instance) return false;

    const refreshed = await instance.updateToken(70);
    persistTokens(instance);
    set({
      user: tokenUser(instance.tokenParsed),
      isAuthenticated: Boolean(instance.authenticated),
      keycloak: instance,
    });
    return refreshed;
  },

  hasRole: (role) => {
    return Boolean(keycloak?.hasRealmRole?.(role));
  },

  register: async () => {
    throw new Error("Accounts are pre-provisioned by an administrator");
  },

  fetchMe: async () => get().user,
}));

export { getStoredRealmConfig, keycloak };
